import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowBack } from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { AssetsIllustration } from '../../illustrations/form_builder/AssetsIllustration';
import { CustomListIllustration } from '../../illustrations/form_builder/CustomListIllustration';
import { LocationsIllustration } from '../../illustrations/form_builder/LocationsIllustration';
import { DataListSource, QuestionUnsafe } from '../../types';
import { datascopeTracker } from '../../utils/eventTracker';
import { useLocationsSelectorState } from '../Locations/LocationsSelector/LocationsSelectorReducer';
import { SelectedStatus } from '../Locations/utils/LocationsReducer';
import { Modal } from '../uiComponents/modal';
import { SearchBar } from '../uiComponents/searchBar';
import { FormBuilderContext } from './FormBuilderProvider';
import { SearchDataListModalActions } from './searchDataListModal/searchDataListModalActions';
import { SearchDataListModalList } from './searchDataListModal/searchDataListModalList';

const customStyles = {
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  backButton: {
    cursor: 'pointer',
  },
} satisfies Record<string, SxProps<Theme>>;

export enum ListTabs {
  CustomList = 'CUSTOM_LISTS',
  Locations = 'LOCATIONS',
  Assets = 'ASSETS',
}

export type Category = {
  id: ListTabs;
  title: string;
  description: string;
  icon: JSX.Element;
};

interface SearchDataListModalProps {
  dataSources: DataListSource[];
  onSelect: (id: string, value: string, tableId?: string) => void;
  questions: QuestionUnsafe[];
  onNestedLocationsChange: (id: string, selectedLocations: Map<number, SelectedStatus>) => void;
  onNestedLocationsAllSelected: (id: string, allLocationsSelected: boolean) => void;
  onNestedLocationsInTableChange: (
    id: string,
    selectedLocations: Map<number, SelectedStatus>,
    tableId: string,
  ) => void;
}

export const SearchDataListModal = memo(
  ({
    dataSources,
    onSelect,
    questions,
    onNestedLocationsChange,
    onNestedLocationsInTableChange,
    onNestedLocationsAllSelected,
  }: SearchDataListModalProps) => {
    const lang = useLanguage();
    const { formId } = useParams<{ formId: string }>();
    const {
      searchDataListModalOpen,
      setSearchDataListModalOpen,
      setListModalQuestionId,
      listModalQuestionId,
      setListModalTableId,
      listModalTableId,
    } = useContext(FormBuilderContext);
    const { checkFlag } = useFeatureFlags();

    const dataListsUsedInForm = useMemo(() => {
      const uniqueMetadataSet = questions.reduce<Set<string>>((set, question) => {
        if (question.metadata) {
          set.add(question.metadata);
        }

        if (question.questions) {
          const nestedMetadata = question.questions
            .filter((nestedQuestion) => nestedQuestion.metadata)
            .map((nestedQuestion) => nestedQuestion.metadata);
          nestedMetadata.forEach((metadata) => set.add(metadata));
        }

        return set;
      }, new Set<string>());

      return Array.from(uniqueMetadataSet);
    }, [questions]);

    const sourcesInForm = useMemo(() => {
      return dataSources.filter(({ value }) =>
        dataListsUsedInForm.some((sourceVal) => sourceVal === value),
      );
    }, [dataSources, dataListsUsedInForm]);

    const orderSources = useMemo(() => {
      const sourcesWithoutFirst = dataSources
        .slice(1)
        .filter(({ value }) => !sourcesInForm.some((source) => source.value === value));
      const ordered = sourcesWithoutFirst.sort((a, b) => a.tag.localeCompare(b.tag));
      return [dataSources[0], ...ordered];
    }, [dataSources, sourcesInForm]);

    const [selected, setSelected] = useState('');
    const [dataSourcesList, setDataSourcesList] = useState(orderSources);
    const [dataSourcesInForm, setDataSourcesInForm] = useState(sourcesInForm);
    const [searchQuery, setSearchQuery] = useState('');

    // Track when modal is opened
    useEffect(() => {
      if (searchDataListModalOpen) {
        datascopeTracker(
          'open_lists_modal',
          {
            form_id: formId,
            open_modal: true,
          },
          'AMP',
        );
      }
    }, [searchDataListModalOpen]);

    useEffect(() => {
      setDataSourcesList(orderSources);
    }, [orderSources]);

    useEffect(() => {
      setDataSourcesInForm(sourcesInForm);
    }, [sourcesInForm]);

    const handleClose = () => {
      setSearchDataListModalOpen(false);
      setSelected('');
      setListModalQuestionId(null);
      setListModalTableId(null);
      setActiveTab(null);
    };

    const handleSearch = (query: string) => {
      setDataSourcesList(
        orderSources.filter(({ tag }) => tag.toLowerCase().includes(query.toLowerCase())),
      );
      setDataSourcesInForm(
        sourcesInForm.filter(({ tag }) => tag.toLowerCase().includes(query.toLowerCase())),
      );
      setSearchQuery(query);
    };

    const handleConfirm = (selectedValue?: string) => {
      // This is to avoid race condition between the selected value and the selected list id
      const selectedListId = selectedValue ?? selected;
      const selectedSource = dataSources.find((source) => source.value === selectedListId);

      datascopeTracker(
        'apply_selection',
        {
          form_id: formId,
          list_id: selectedListId,
          assets_list: activeTab === ListTabs.Locations ? 'locations' : selectedSource?.tag,
        },
        'AMP',
      );

      if (activeTab === ListTabs.Locations) {
        handleNestedLocationsChange();
      } else {
        onSelect(listModalQuestionId, selectedListId, listModalTableId);
      }
      handleClose();
    };

    const handleSelectItem = (value: string) => {
      const selectedSource = dataSources.find((source) => source.value === value);

      datascopeTracker(
        'select_list_items',
        {
          form_id: formId,
          assets_list: selectedSource?.tag,
          list_id: value,
        },
        'AMP',
      );

      setSelected(value);
    };

    const hasNestableLocationsFlag = checkFlag('NestableLocations');

    const categories: Category[] = useMemo(() => {
      const data = [
        {
          id: ListTabs.CustomList,
          title: lang.form_builder.customLists,
          description: lang.form_builder.customListsDescription,
          icon: <CustomListIllustration />,
        },
      ];

      if (hasNestableLocationsFlag) {
        data.push({
          id: ListTabs.Locations,
          title: lang.form_builder.locations,
          description: lang.form_builder.locationsDescription,
          icon: <LocationsIllustration />,
        });
      }

      if (dataSources.some((source) => source.value === 'assets')) {
        data.push({
          id: ListTabs.Assets,
          title: lang.mainMenu.assets,
          description: lang.form_builder.assetsDescription,
          icon: <AssetsIllustration />,
        });
      }

      return data;
    }, [hasNestableLocationsFlag, dataSourcesList]);

    useEffect(() => {
      /* If there is only one category, set the active tab to CUSTOM_LISTS to improve UX */
      if (categories.length === 1) {
        setActiveTab(ListTabs.CustomList);
      }
    }, [categories.length]);

    const currentQuestion = useMemo(() => {
      if (!listModalQuestionId) return null;

      // First check top-level questions
      const topLevelQuestion = questions.find((q) => q.id === listModalQuestionId);
      if (topLevelQuestion) return topLevelQuestion;

      // Then check nested questions in tables
      for (const question of questions) {
        if (question.questions) {
          const nestedQuestion = question.questions.find((q) => q.id === listModalQuestionId);
          if (nestedQuestion) return nestedQuestion;
        }
      }

      return null;
    }, [listModalQuestionId, questions]);

    const { locationsState, handleChangeLocationSelection, handleChangeAllLocationsSelection } =
      useLocationsSelectorState({
        initialSelectedLocations: currentQuestion?.nestableLocations?.selectedLocations,
        initialAllLocationsSelected: currentQuestion?.nestableLocations?.all_locations_selected,
        multipleSelection: true,
      });

    const [activeTab, setActiveTab] = useState<ListTabs | null>(null);

    const handleNestedLocationsChange = () => {
      if (!listModalQuestionId) return;
      if (locationsState.allLocationsSelected)
        return onNestedLocationsAllSelected(listModalQuestionId, true);

      if (listModalTableId) {
        onNestedLocationsInTableChange(
          listModalQuestionId,
          locationsState.selectedLocations,
          listModalTableId,
        );
      } else {
        onNestedLocationsChange(listModalQuestionId, locationsState.selectedLocations);
      }
    };

    const anyLocationSelected =
      Array.from(locationsState.selectedLocations.values()).some((status) =>
        ['selected', 'onlyChildren'].includes(status),
      ) || locationsState.allLocationsSelected;

    const confirmButtonDisabled = useMemo(() => {
      if (activeTab === ListTabs.Locations) {
        if (anyLocationSelected) {
          return false;
        }
        return true;
      }
      return !selected;
    }, [selected, activeTab, anyLocationSelected]);

    return (
      <Modal
        open={searchDataListModalOpen}
        onClose={handleClose}
        titleStartIcon={
          activeTab !== null &&
          categories.length > 1 && (
            <ArrowBack
              sx={customStyles.backButton}
              onClick={() => setActiveTab(null)}
            />
          )
        }
        title={lang.form_builder.dataListSearchModalTitle}
        header={
          activeTab !== null && (
            <SearchBar
              placeholder={lang.form_builder.searchInListPlaceholder}
              onSearch={handleSearch}
            />
          )
        }
        content={
          <SearchDataListModalList
            sourcesList={dataSourcesList}
            onSelect={handleSelectItem}
            selected={selected}
            sourcesInForm={dataSourcesInForm}
            searchQuery={searchQuery}
            locationsState={locationsState}
            handleChangeLocationSelection={handleChangeLocationSelection}
            handleChangeAllLocationsSelection={handleChangeAllLocationsSelection}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            anyLocationSelected={anyLocationSelected}
            handleConfirm={handleConfirm}
            categories={categories}
          />
        }
        actions={
          activeTab !== null && (
            <SearchDataListModalActions
              onClose={handleClose}
              onConfirm={handleConfirm}
              confirmButtonDisabled={confirmButtonDisabled}
              activeTab={activeTab}
            />
          )
        }
      />
    );
  },
);
